import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "@/auth/useAuth";
import { Field } from "@/components/Field";
import {
  fetchMyProfile,
  updateMyProfile,
  type MyProfile,
  type UpdatableProfileFields,
  type UpdateProfileResult,
} from "@/services/graphService";
import { AppShell } from "@/layout/AppShell";
import { isCiamTenant } from "@/utils/tenant";

type Status =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "saving" }
  | { kind: "error"; message: string }
  | { kind: "success"; result: UpdateProfileResult };

export function ProfilePage() {
  return (
    <AppShell>
      <ProfileContent />
    </AppShell>
  );
}

function ProfileContent() {
  const { account, acquireGraphToken } = useAuth();
  const ciam = isCiamTenant();

  const [profile, setProfile] = useState<MyProfile | null>(null);
  const [draft, setDraft] = useState<UpdatableProfileFields>({});
  const [status, setStatus] = useState<Status>({ kind: "loading" });

  const load = useCallback(async () => {
    try {
      setStatus({ kind: "loading" });
      const token = await acquireGraphToken();
      if (!token?.accessToken) {
        setStatus({
          kind: "error",
          message:
            "Could not acquire a Microsoft Graph token. Ensure User.Read / User.ReadWrite is consented on the SPA app registration.",
        });
        return;
      }
      const me = await fetchMyProfile(token.accessToken);
      setProfile(me);
      setDraft({
        displayName: me.displayName ?? "",
        givenName: me.givenName ?? "",
        surname: me.surname ?? "",
        mail: me.mail ?? "",
        jobTitle: me.jobTitle ?? "",
        mobilePhone: me.mobilePhone ?? "",
      });
      setStatus({ kind: "idle" });
    } catch (err) {
      setStatus({
        kind: "error",
        message: err instanceof Error ? err.message : "Failed to load profile",
      });
    }
  }, [acquireGraphToken]);

  useEffect(() => {
    void load();
  }, [load]);

  const diff = useMemo<UpdatableProfileFields>(() => {
    if (!profile) return {};
    const out: UpdatableProfileFields = {};
    for (const key of Object.keys(draft) as (keyof UpdatableProfileFields)[]) {
      const original = (profile[key] ?? "") as string;
      const updated = (draft[key] ?? "") as string;
      if (original.trim() !== updated.trim()) {
        out[key] = updated.trim() ? updated.trim() : null;
      }
    }
    // In CIAM we never send `mail` even if changed – it's admin-managed.
    if (ciam) delete out.mail;
    return out;
  }, [draft, profile, ciam]);

  const hasChanges = Object.keys(diff).length > 0;

  const save = async () => {
    try {
      setStatus({ kind: "saving" });
      const token = await acquireGraphToken();
      if (!token?.accessToken) throw new Error("No Graph token");
      const result = await updateMyProfile(token.accessToken, diff);
      await load();
      setStatus({ kind: "success", result });
    } catch (err) {
      setStatus({
        kind: "error",
        message: err instanceof Error ? err.message : "Update failed",
      });
    }
  };

  const upn = account?.username ?? profile?.userPrincipalName ?? "";

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-6">
      <header>
        <Link
          to="/myapps"
          className="text-xs font-medium text-brand-700 hover:underline"
        >
          ← Back to My Apps
        </Link>
        <h1 className="mt-2 text-2xl font-semibold text-slate-900">
          My profile
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          {ciam
            ? "Update your name and contact number. Your email is managed by your administrator."
            : "Update the personal details your organisation uses to reach you."}
        </p>
      </header>

      {status.kind === "loading" ? (
        <div className="card p-6 text-sm text-slate-500">
          Loading your profile…
        </div>
      ) : status.kind === "error" ? (
        <div className="card border-red-200 p-6">
          <div className="text-sm font-semibold text-red-700">
            {status.message}
          </div>
          <button type="button" onClick={load} className="btn-secondary mt-3">
            Retry
          </button>
        </div>
      ) : (
        <>
          <section className="card space-y-4 p-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field
                label="Given name"
                value={draft.givenName ?? ""}
                onChange={(v) => setDraft((d) => ({ ...d, givenName: v }))}
              />
              <Field
                label="Surname"
                value={draft.surname ?? ""}
                onChange={(v) => setDraft((d) => ({ ...d, surname: v }))}
              />
            </div>
            <Field
              label="Display name"
              value={draft.displayName ?? ""}
              onChange={(v) => setDraft((d) => ({ ...d, displayName: v }))}
              hint="How your name appears in apps, emails and notifications."
            />
            <Field
              label="Email"
              type="email"
              value={draft.mail ?? ""}
              onChange={(v) => setDraft((d) => ({ ...d, mail: v }))}
              readOnly={ciam}
              badge={ciam ? "read-only" : undefined}
              hint={
                ciam
                  ? "In Entra External ID the mail attribute is admin-managed. Contact your administrator to change it."
                  : undefined
              }
            />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Field
                label="Mobile phone"
                type="tel"
                value={draft.mobilePhone ?? ""}
                onChange={(v) => setDraft((d) => ({ ...d, mobilePhone: v }))}
              />
              <Field
                label="Job title"
                value={draft.jobTitle ?? ""}
                onChange={(v) => setDraft((d) => ({ ...d, jobTitle: v }))}
              />
            </div>

            <div className="border-t border-slate-100 pt-4">
              <Field
                label="Login (userPrincipalName)"
                value={upn}
                readOnly
                badge="read-only"
                hint="Use this address to sign in. Managed by your administrator."
              />
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-slate-100 pt-4">
              <div className="text-xs text-slate-500">
                {hasChanges ? "You have unsaved changes." : "No changes yet."}
              </div>
              <div className="flex items-center gap-2">
                <Link to="/myapps/change-password" className="btn-ghost text-xs">
                  Change password
                </Link>
                <button
                  type="button"
                  onClick={save}
                  disabled={!hasChanges || status.kind === "saving"}
                  className="btn-primary"
                >
                  {status.kind === "saving" ? "Saving…" : "Save changes"}
                </button>
              </div>
            </div>
          </section>

          {status.kind === "success" ? (
            <SuccessNotice result={status.result} />
          ) : null}
        </>
      )}
    </div>
  );
}

function SuccessNotice({ result }: { result: UpdateProfileResult }) {
  const { applied, skipped } = result;
  return (
    <div className="card p-4">
      {applied.length > 0 ? (
        <div className="text-sm text-emerald-700">
          ✔ Saved: {applied.join(", ")}
        </div>
      ) : null}
      {skipped.length > 0 ? (
        <div className="mt-2 text-xs text-amber-800">
          <div className="font-semibold">Some fields were not updated:</div>
          <ul className="mt-1 list-disc pl-5">
            {skipped.map((s) => (
              <li key={s.field}>
                <span className="font-medium">{s.field}</span>: {s.reason}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
